import React from 'react';
import { observer } from 'mobx-react-lite';
import { useStores } from 'src/components/store';
import { VersionLabel } from 'src/utils/enums';
import { SelectionButton } from 'src/components/shared/selection-button/selection-button';
import CustomIcon, { IconType } from 'src/components/shared/custom-icon/custom-icon';
import CustomButton from 'src/components/shared/custom-button/custom-button';
import useCustomModal from 'src/components/shared/custom-modal/custom-modal';
import { useNavigate } from 'src/utils/use-navigate';
import { EventType } from '../../../dtos/analytics.dto';

interface VersionButtonHoveringProps {
    label: VersionLabel | undefined;
    isShowPartialLabels?: boolean;
    setIsMenuOpen: (isMenuOpen: boolean) => void;
    onApplyPolicy: () => void;
    onRestorePolicy: () => void;
    onMouseLeave: () => void;
}

export const VersionButtonHovering: React.FC<VersionButtonHoveringProps> =
    observer(({ label, isShowPartialLabels, setIsMenuOpen, onApplyPolicy, onRestorePolicy, onMouseLeave }) => {
        const { fwVersionStore, customerStore } = useStores();
        const [showModal, , contextHolder] = useCustomModal();
        const navigate = useNavigate();

        const removeDraft = () => {
            const draftId = fwVersionStore.draftVersion?.externalId;
            if (draftId) {
                const shouldReset = customerStore.isSmartPolicySecondVersionDraftReady || customerStore.isQuickStartDraftReady;
                const subTitle = shouldReset ?
                    'Deleting the draft policy will redirect you to the policy selection page. Are you sure you want to delete the draft policy?' :
                    'You will not be able to restore the draft once deleted.';
                showModal({
                    wrapClassName: 'delete-modal-wrapper',
                    width:  562,
                    title: 'Delete Draft?',
                    body: () => <div className="modal-commit-message">{subTitle}</div>,
                    okText: 'Delete',
                    cancelText: 'Cancel',
                    onOk: async () => {
                        fwVersionStore.sendGeneralEvent(EventType.LinkClicked, { button: 'delete' });
                        if (shouldReset) {
                            await customerStore.rollbackPolicyMode([customerStore.serviceId]);
                        } else {
                            await fwVersionStore.deleteVersion(draftId);
                            if (!isShowPartialLabels) {
                                navigate(`/${customerStore.customerId}/${customerStore.serviceId}/protection/version`, { replace: true });
                            }
                        }
                    },
                    onCancel: onMouseLeave,
                    closable: false
                });
            }
        };

        return <>
            {label === VersionLabel.Inactive ?
                <CustomButton className={`version-indication-btn primary-btn restore`}
                              onClick={onRestorePolicy} label="Restore" prefixIcon={<CustomIcon type={IconType.RESTORE} />} /> :
                <SelectionButton
                    options={[{ key: '1', label: 'Delete Draft', icon: IconType.TRASH }]}
                    onClick={onApplyPolicy}
                    onSelect={removeDraft}
                    label={'Apply draft'} width={223}
                    openChangeCb={setIsMenuOpen}
                    disable={!customerStore.isAvailable}
                    enableSelect={true} />}
            {contextHolder}
        </>;
    });